import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useLiveQuery } from 'dexie-react-hooks';
import { useStore } from '../store/useStore';
import { useCommands } from '../lib/commands';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { listDocumentMeta } from '../lib/docMeta';
import { matchDocuments } from '../lib/quickOpen';
import { useT } from '../i18n/useT';

/**
 * Command palette (cmdk). Every primary action is reachable here with its
 * shortcut shown, plus quick-open for any document in the library.
 */
export function CommandPalette() {
  const t = useT();
  const open = useStore((s) => s.paletteOpen);
  const mode = useStore((s) => s.paletteMode);
  const setOpen = useStore((s) => s.setPaletteOpen);
  const [search, setSearch] = useState('');
  const documentsOnly = mode === 'documents';

  // Each opening starts from an empty query, whichever mode it opens in.
  useEffect(() => {
    if (open) setSearch('');
  }, [open, mode]);
  const controller = useDocControllerContext();
  const commands = useCommands();
  const docs = useLiveQuery(() => listDocumentMeta(), [], []);
  const title = (id: string) => t(`cmd.${id}`);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof commands>();
    for (const cmd of commands) {
      const list = map.get(cmd.group) ?? [];
      list.push(cmd);
      map.set(cmd.group, list);
    }
    return map;
  }, [commands]);

  // Matched over the whole library, then capped — see matchDocuments.
  const docMatches = useMemo(() => matchDocuments(docs, search), [docs, search]);

  const runAndClose = (fn: () => void | Promise<void>) => {
    setOpen(false);
    void fn();
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(next) => setOpen(next)}
      label={documentsOnly ? t('cmdk.quickOpen') : t('toolbar.palette')}
      className="mb-cmdk"
      // In the quick switcher our own ranking is the order; cmdk's fuzzy
      // re-scoring would reshuffle it. The full palette still filters commands.
      shouldFilter={!documentsOnly}
    >
      <Command.Input
        value={search}
        onValueChange={setSearch}
        placeholder={documentsOnly ? t('cmdk.quickOpenPlaceholder') : t('cmdk.placeholder')}
      />
      <Command.List>
        <Command.Empty>{t('cmdk.empty')}</Command.Empty>

        {!documentsOnly &&
          [...grouped.entries()].map(([group, cmds]) => (
            <Command.Group key={group} heading={t(`grp.${group}`)}>
              {cmds.map((cmd) => (
                <Command.Item
                  key={cmd.id}
                  value={`${title(cmd.id)} ${cmd.keywords ?? ''}`}
                  onSelect={() => runAndClose(cmd.run)}
                >
                  <span>{title(cmd.id)}</span>
                  {cmd.shortcut && <kbd className="mb-cmdk-shortcut">{cmd.shortcut}</kbd>}
                </Command.Item>
              ))}
            </Command.Group>
          ))}

        {docMatches.length > 0 && (
          <Command.Group heading={t('cmdk.openDoc')}>
            {docMatches.map((doc) => (
              <Command.Item
                key={doc.id}
                // Aliases and folder are part of the value so cmdk's filter in
                // the full palette keeps what matchDocuments matched on them.
                value={`open ${doc.title} ${doc.aliases.join(' ')} ${doc.folder ?? ''} ${doc.id}`}
                onSelect={() => runAndClose(() => controller.open(doc.id))}
              >
                <span>{doc.title || t('common.untitled')}</span>
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>
    </Command.Dialog>
  );
}
